import { Box, Typography, Paper, Alert, Chip } from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { stockApi, type StockAvailabilityDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

const columns: GridColDef<StockAvailabilityDto>[] = [
  { field: "sku", headerName: "SKU", width: 120 },
  { field: "itemName", headerName: "Item", flex: 1, minWidth: 200 },
  { field: "batchNo", headerName: "Batch", width: 130 },
  {
    field: "expiryDate",
    headerName: "Expiry",
    width: 140,
    renderCell: (p) => {
      if (!p.value) return "—";
      const d = dayjs(p.value);
      const daysLeft = d.diff(dayjs(), "day");
      const color: "default" | "warning" | "error" =
        daysLeft < 0 ? "error" : daysLeft < 90 ? "warning" : "default";
      return <Chip size="small" label={d.format("YYYY-MM-DD")} color={color} variant="outlined" />;
    },
  },
  { field: "warehouseName", headerName: "Warehouse", width: 150 },
  { field: "onHandQty", headerName: "On hand", type: "number", width: 100 },
  { field: "reservedQty", headerName: "Reserved", type: "number", width: 100 },
  {
    field: "availableQty",
    headerName: "Available",
    type: "number",
    width: 110,
    renderCell: (p) => (
      <strong style={{ color: p.value <= 0 ? "#d32f2f" : "#2e7d32" }}>{p.value}</strong>
    ),
  },
  { field: "costPrice", headerName: "Cost", type: "number", width: 100 },
  { field: "mrp", headerName: "MRP", type: "number", width: 100 },
];

export function StockPage() {
  const query = useQuery({
    queryKey: ["stock-availability"],
    queryFn: () => stockApi.availability(),
  });

  return (
    <Box>
      <PageHeader breadcrumbs={["Inventory"]} title="Stock availability" />

      <Paper sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Available = Σ(ledger entries) − Σ(active reservations). All quantities are derived from the
          immutable inventory ledger, never stored on the batch row.
        </Typography>

        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}

        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data ?? []}
            columns={columns}
            loading={query.isLoading}
            disableRowSelectionOnClick
            getRowId={(row) => `${row.batchId}-${row.warehouseId}`}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </Box>
      </Paper>
    </Box>
  );
}
